import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  FloppyDisk,
  Buildings,
  CreditCard,
  FileText,
  Hash,
  GoogleDriveLogo,
  CheckCircle,
  WarningCircle,
  DeviceMobile,
  AppleLogo,
  AndroidLogo,
  Bell
} from "@phosphor-icons/react";
import { Envelope } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="card p-6">
    <h2 className="text-heading-3 mb-4 flex items-center gap-2">
      <Icon size={20} className="text-blue-600" /> {title}
    </h2>
    {children}
  </div>
);

const Settings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveLoading, setDriveLoading] = useState(true);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailLoading, setGmailLoading] = useState(true);
  const [alertSettings, setAlertSettings] = useState({
    task_blocked_days: 2,
    task_overdue_days: 0,
    chez_prestataire_days: 7,
    task_no_progress_days: 5,
  });
  const [savingAlerts, setSavingAlerts] = useState(false);

  const canEdit = user?.role === "admin";

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDriveStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/drive/status`);
      setDriveStatus(response.data);
    } catch {
      setDriveStatus({ connected: false });
    } finally {
      setDriveLoading(false);
    }
  }, []);

  const fetchGmailStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gmail/status`);
      setGmailStatus(response.data);
    } catch {
      setGmailStatus({ connected: false });
    } finally {
      setGmailLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); fetchDriveStatus(); fetchGmailStatus(); fetchAlertSettings(); }, [fetchSettings, fetchDriveStatus, fetchGmailStatus]);

  const fetchAlertSettings = async () => {
    try {
      const res = await axios.get(`${API}/alerts/active`);
      if (res.data?.thresholds) {
        setAlertSettings(prev => ({ ...prev, ...res.data.thresholds }));
      }
    } catch {
      // Use defaults
    }
  };

  const handleSaveAlertSettings = async () => {
    setSavingAlerts(true);
    try {
      await axios.put(`${API}/alerts/settings`, alertSettings);
      toast.success("Seuils d'alerte mis a jour");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingAlerts(false);
    }
  };

  // Check for drive/gmail connection callbacks
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive") === "connected") {
      toast.success("Google Drive connecte avec succes !");
      fetchDriveStatus();
      window.history.replaceState({}, "", "/parametres");
    } else if (params.get("drive") === "error") {
      toast.error("Erreur de connexion Google Drive");
      window.history.replaceState({}, "", "/parametres");
    }
    if (params.get("gmail") === "connected") {
      toast.success("Gmail connecte avec succes !");
      fetchGmailStatus();
      window.history.replaceState({}, "", "/parametres");
    } else if (params.get("gmail") === "error") {
      toast.error("Erreur de connexion Gmail");
      window.history.replaceState({}, "", "/parametres");
    }
  }, [fetchDriveStatus, fetchGmailStatus]);

  const handleConnectDrive = async () => {
    try {
      const response = await axios.get(`${API}/drive/connect`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error("Erreur lors de la connexion a Google Drive");
    }
  };

  const handleConnectGmail = async () => {
    try {
      const response = await axios.get(`${API}/gmail/connect`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error("Erreur lors de la connexion a Gmail");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/company`, settings);
      toast.success("Paramètres enregistrés");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => setSettings({ ...settings, [field]: value });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-10 w-10"></div></div>;
  }

  return (
    <div data-testid="settings-page" className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Paramètres</h1>
          <p className="page-subtitle">Configuration de la société et de la facturation</p>
        </div>
        {canEdit && (
          <div className="page-actions">
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-settings-btn">
              <FloppyDisk size={18} className="mr-2" />
              {saving ? "Enregistrement..." : "Enregistrer tout"}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Société */}
        <SectionCard icon={Buildings} title="Informations société">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Nom de la société</Label>
              <Input value={settings.nom_societe || ""} onChange={(e) => updateField("nom_societe", e.target.value)} disabled={!canEdit} data-testid="input-nom-societe" />
            </div>
            <div>
              <Label className="form-label">Forme juridique</Label>
              <Input value={settings.forme_juridique || ""} onChange={(e) => updateField("forme_juridique", e.target.value)} disabled={!canEdit} placeholder="SRL, SA, SPRL..." />
            </div>
            <div className="md:col-span-2">
              <Label className="form-label">Adresse (siège d'exploitation)</Label>
              <Input value={settings.adresse || ""} onChange={(e) => updateField("adresse", e.target.value)} disabled={!canEdit} data-testid="input-adresse" />
            </div>
            <div>
              <Label className="form-label">Code postal</Label>
              <Input value={settings.code_postal || ""} onChange={(e) => updateField("code_postal", e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label className="form-label">Ville</Label>
              <Input value={settings.ville || ""} onChange={(e) => updateField("ville", e.target.value)} disabled={!canEdit} />
            </div>
            <div className="md:col-span-2">
              <Label className="form-label">Siège social</Label>
              <Input value={settings.siege_social || ""} onChange={(e) => updateField("siege_social", e.target.value)} disabled={!canEdit} data-testid="input-siege-social" placeholder="Adresse du siège social si différente" />
            </div>
            <div>
              <Label className="form-label">Téléphone</Label>
              <Input value={settings.telephone || ""} onChange={(e) => updateField("telephone", e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label className="form-label">Email</Label>
              <Input type="email" value={settings.email || ""} onChange={(e) => updateField("email", e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label className="form-label">Site web</Label>
              <Input value={settings.site_web || ""} onChange={(e) => updateField("site_web", e.target.value)} disabled={!canEdit} placeholder="https://..." />
            </div>
          </div>
        </SectionCard>

        {/* Fiscal */}
        <SectionCard icon={Hash} title="Informations fiscales">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Numéro de TVA</Label>
              <Input value={settings.numero_tva || ""} onChange={(e) => updateField("numero_tva", e.target.value)} disabled={!canEdit} data-testid="input-numero-tva" placeholder="BE 0xxx.xxx.xxx" />
            </div>
            <div>
              <Label className="form-label">Numéro d'entreprise (BCE)</Label>
              <Input value={settings.numero_entreprise || ""} onChange={(e) => updateField("numero_entreprise", e.target.value)} disabled={!canEdit} placeholder="0xxx.xxx.xxx" />
            </div>
          </div>
        </SectionCard>

        {/* Bancaire */}
        <SectionCard icon={CreditCard} title="Informations bancaires">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="form-label">IBAN</Label>
              <Input value={settings.iban || ""} onChange={(e) => updateField("iban", e.target.value)} disabled={!canEdit} placeholder="BE00 0000 0000 0000" />
            </div>
            <div>
              <Label className="form-label">BIC</Label>
              <Input value={settings.bic || ""} onChange={(e) => updateField("bic", e.target.value)} disabled={!canEdit} placeholder="GEBABEBB" />
            </div>
            <div>
              <Label className="form-label">Représentant légal</Label>
              <Input value={settings.representant || ""} onChange={(e) => updateField("representant", e.target.value)} disabled={!canEdit} placeholder="Monsieur ..." data-testid="input-representant" />
            </div>
            <div>
              <Label className="form-label">N° national du représentant</Label>
              <Input value={settings.representant_numero_national || ""} onChange={(e) => updateField("representant_numero_national", e.target.value)} disabled={!canEdit} placeholder="00.00.00-000.00" data-testid="input-representant-nn" />
            </div>
          </div>
        </SectionCard>

        {/* Facturation */}
        <SectionCard icon={FileText} title="Paramètres de facturation">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="form-label">Préfixe vente</Label>
              <Input value={settings.prefixe_facture_vente || "FV"} onChange={(e) => updateField("prefixe_facture_vente", e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label className="form-label">Préfixe achat</Label>
              <Input value={settings.prefixe_facture_achat || "FA"} onChange={(e) => updateField("prefixe_facture_achat", e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label className="form-label">Préfixe acompte</Label>
              <Input value={settings.prefixe_acompte || "AC"} onChange={(e) => updateField("prefixe_acompte", e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label className="form-label">Préfixe avoir</Label>
              <Input value={settings.prefixe_avoir || "AV"} onChange={(e) => updateField("prefixe_avoir", e.target.value)} disabled={!canEdit} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="form-label">Conditions de paiement par défaut</Label>
              <Input value={settings.conditions_paiement_defaut || ""} onChange={(e) => updateField("conditions_paiement_defaut", e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label className="form-label">Délai de paiement (jours)</Label>
              <Input type="number" value={settings.delai_paiement_jours || 30} onChange={(e) => updateField("delai_paiement_jours", parseInt(e.target.value) || 30)} disabled={!canEdit} />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="form-label">Conditions Générales de Vente (affichées sur les bons de commande)</Label>
              <Textarea value={settings.cgdv_text || ""} onChange={(e) => updateField("cgdv_text", e.target.value)} disabled={!canEdit} rows={6} data-testid="input-cgdv" placeholder="Vos CGV seront automatiquement imprimées sur chaque bon de commande..." />
            </div>
            <div>
              <Label className="form-label">Mentions légales facture avec garantie</Label>
              <Textarea value={settings.mentions_facture_garantie || ""} onChange={(e) => updateField("mentions_facture_garantie", e.target.value)} disabled={!canEdit} rows={3} />
            </div>
            <div>
              <Label className="form-label">Mentions légales facture sans garantie</Label>
              <Textarea value={settings.mentions_facture_sans_garantie || ""} onChange={(e) => updateField("mentions_facture_sans_garantie", e.target.value)} disabled={!canEdit} rows={3} />
            </div>
          </div>
        </SectionCard>

        {/* Google Drive */}
        <SectionCard icon={GoogleDriveLogo} title="Google Drive">
          {driveLoading ? (
            <div className="flex items-center gap-2 text-neutral-500"><div className="spinner h-4 w-4"></div> Vérification...</div>
          ) : driveStatus?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle size={20} weight="fill" />
                <span className="font-medium">Connecté</span>
              </div>
              <div className="text-sm text-neutral-600">
                <p>Compte : <span className="font-medium">{driveStatus.email}</span></p>
                {driveStatus.display_name && <p>Nom : {driveStatus.display_name}</p>}
              </div>
              <p className="text-xs text-neutral-500">
                Les dossiers Photos/ et Documents/ sont créés automatiquement pour chaque véhicule dans le dossier "Stock Auto".
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-amber-600">
                <WarningCircle size={20} weight="fill" />
                <span className="font-medium">Non connecté</span>
              </div>
              <p className="text-sm text-neutral-600">
                Connectez votre Google Drive pour synchroniser automatiquement les photos et documents de vos véhicules.
              </p>
              {canEdit && (
                <Button onClick={handleConnectDrive} data-testid="connect-drive-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <GoogleDriveLogo size={18} className="mr-2" />
                  Connecter Google Drive
                </Button>
              )}
            </div>
          )}
        </SectionCard>

        {/* Gmail */}
        <SectionCard icon={Envelope} title="Gmail">
          {gmailLoading ? (
            <div className="flex items-center gap-2 text-neutral-500"><div className="spinner h-4 w-4"></div> Verification...</div>
          ) : gmailStatus?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle size={20} weight="fill" />
                <span className="font-medium">Connecte</span>
              </div>
              <p className="text-xs text-neutral-500">
                Vous pouvez envoyer des emails et factures directement depuis l'application.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-amber-600">
                <WarningCircle size={20} weight="fill" />
                <span className="font-medium">Non connecte</span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Connectez votre compte Gmail pour envoyer des emails, factures et annonces directement depuis MikAuto.
              </p>
              {canEdit && (
                <Button onClick={handleConnectGmail} data-testid="connect-gmail-btn" className="bg-red-600 hover:bg-red-700 text-white">
                  <Envelope size={18} className="mr-2" />
                  Connecter Gmail
                </Button>
              )}
            </div>
          )}
        </SectionCard>

        {/* Alert Settings */}
        <SectionCard icon={Bell} title="Rappels automatiques">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Configurez les seuils pour recevoir des alertes automatiques quand une tâche ou un véhicule nécessite votre attention.
          </p>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Tâche bloquée (jours)</Label>
                <Input type="number" min="1" max="30" data-testid="alert-blocked-days"
                  value={alertSettings.task_blocked_days}
                  onChange={e => setAlertSettings({ ...alertSettings, task_blocked_days: parseInt(e.target.value) || 2 })}
                  disabled={!canEdit} />
                <p className="text-xs text-slate-400 mt-1">Alerte si une tâche est bloquée depuis X jours</p>
              </div>
              <div>
                <Label className="form-label">Retard toléré (jours)</Label>
                <Input type="number" min="0" max="30" data-testid="alert-overdue-days"
                  value={alertSettings.task_overdue_days}
                  onChange={e => setAlertSettings({ ...alertSettings, task_overdue_days: parseInt(e.target.value) || 0 })}
                  disabled={!canEdit} />
                <p className="text-xs text-slate-400 mt-1">Alerte si une tâche dépasse sa date fin prévue de X jours</p>
              </div>
              <div>
                <Label className="form-label">Chez prestataire (jours)</Label>
                <Input type="number" min="1" max="60" data-testid="alert-prestataire-days"
                  value={alertSettings.chez_prestataire_days}
                  onChange={e => setAlertSettings({ ...alertSettings, chez_prestataire_days: parseInt(e.target.value) || 7 })}
                  disabled={!canEdit} />
                <p className="text-xs text-slate-400 mt-1">Alerte si un véhicule est chez un prestataire depuis X jours</p>
              </div>
              <div>
                <Label className="form-label">Sans progression (jours)</Label>
                <Input type="number" min="1" max="30" data-testid="alert-stale-days"
                  value={alertSettings.task_no_progress_days}
                  onChange={e => setAlertSettings({ ...alertSettings, task_no_progress_days: parseInt(e.target.value) || 5 })}
                  disabled={!canEdit} />
                <p className="text-xs text-slate-400 mt-1">Alerte si une tâche «À faire» n'avance pas depuis X jours</p>
              </div>
            </div>
            {canEdit && (
              <Button onClick={handleSaveAlertSettings} disabled={savingAlerts} className="btn-primary" data-testid="save-alert-settings">
                <FloppyDisk size={18} className="mr-2" />
                {savingAlerts ? "Sauvegarde..." : "Sauvegarder les seuils"}
              </Button>
            )}
          </div>
        </SectionCard>

        {/* PWA Installation */}
        <SectionCard icon={DeviceMobile} title="Application mobile (PWA)">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Installez MikAuto sur votre téléphone ou tablette pour un accès rapide, comme une application native.
          </p>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <AppleLogo size={20} className="text-slate-700 dark:text-slate-300" weight="fill" />
                <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">iPhone / iPad (Safari)</h3>
              </div>
              <ol className="text-sm text-slate-600 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
                <li>Ouvrez cette page dans <strong>Safari</strong></li>
                <li>Appuyez sur le bouton <strong>Partager</strong> (carré avec flèche)</li>
                <li>Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></li>
                <li>Appuyez sur <strong>"Ajouter"</strong></li>
              </ol>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <AndroidLogo size={20} className="text-green-600" weight="fill" />
                <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">Android (Chrome)</h3>
              </div>
              <ol className="text-sm text-slate-600 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
                <li>Ouvrez cette page dans <strong>Chrome</strong></li>
                <li>Appuyez sur le menu <strong>&#x22ee;</strong> (3 points verticaux)</li>
                <li>Appuyez sur <strong>"Installer l'application"</strong> ou <strong>"Ajouter à l'écran d'accueil"</strong></li>
                <li>Confirmez en appuyant sur <strong>"Installer"</strong></li>
              </ol>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default Settings;
